import { Button, Input, Tooltip } from "antd";
import {
  ArrowLeftOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import PropTypes from "prop-types";
import React from "react";
import { isEmpty } from "lodash";
import CustomSkeleton from "../../../commonComponents/CustomSkeleton";
import { MaximizeIcon, MinimizeIcon } from "./PanelIcons";
import { AI_RESPONSE_COPY } from "./constants";

/**
 * Common Hide/Unhide view for the Customize AI response experience (EAI-1497).
 * Rendered INLINE inside the same container as the selection panel — under the
 * popover when minimized, under the drawer when maximized — so only the outer
 * container differs. Each Tip / Tone carries an eye toggle: open = shown,
 * slashed = hidden. "Save" persists the hidden set (everything not shown).
 */
export default function HideUnhidePanel({
  isMaximized,
  onToggleMaximize,
  onBack,
  loading,
  tips,
  tones,
  shownTipIds,
  shownToneIds,
  onToggleTip,
  onToggleTone,
  onSave,
  saving,
  additionalCommentsEnabled,
}) {
  const EyeToggle = ({ shown }) =>
    shown ? (
      <EyeOutlined className="text-brand-600" />
    ) : (
      <EyeInvisibleOutlined className="text-grey-400" />
    );

  return (
    <div className="flex flex-col flex-1 min-h-0" onClick={e => e.stopPropagation()}>
      {/* Header — identical across popover, drawer, and the hide/unhide view */}
      <div className="flex items-center justify-between p-3 bg-[#F5F5F5] border-0 border-b border-solid border-[#D9D9D9]">
        <span className="text-sm font-[500] text-grey-700">{AI_RESPONSE_COPY.CUSTOMIZE_TITLE}</span>
        <Tooltip title={isMaximized ? AI_RESPONSE_COPY.MINIMIZE : AI_RESPONSE_COPY.MAXIMIZE}>
          <span className="flex items-center cursor-pointer" onClick={onToggleMaximize}>
            {isMaximized ? <MinimizeIcon /> : <MaximizeIcon />}
          </span>
        </Tooltip>
      </div>

      {/* Body */}
      <div className="flex flex-col gap-3 px-4 py-3 flex-1 min-h-0 overflow-y-auto">
        <button
          type="button"
          className="flex items-center gap-1 self-start bg-transparent border-0 p-0 cursor-pointer text-sm text-grey-600 hover:text-grey-800"
          onClick={onBack}
        >
          <ArrowLeftOutlined className="text-xs" /> {AI_RESPONSE_COPY.BACK}
        </button>

        <div className="flex flex-col items-start gap-1">
          <div className="flex flex-row items-center justify-between w-full">
            <span className="text-sm font-[400] text-grey-700">{AI_RESPONSE_COPY.QUICK_TIPS}</span>
            <button
              type="button"
              className="flex items-center gap-1 bg-transparent border-0 p-0 cursor-pointer text-sm text-brand-600 disabled:opacity-50"
              disabled={saving}
              onClick={onSave}
            >
              {saving && <LoadingOutlined spin />}
              {AI_RESPONSE_COPY.SAVE}
            </button>
          </div>
          <div className="flex flex-col w-full">
            {loading ? (
              <CustomSkeleton skeletonCount={1} />
            ) : !isEmpty(tips) ? (
              tips.map(item => {
                const shown = shownTipIds.includes(item.id);
                return (
                  <button
                    key={item.id}
                    type="button"
                    className="flex items-center gap-2 w-full py-1 text-left bg-transparent border-0 cursor-pointer"
                    onClick={() => onToggleTip(item.id)}
                  >
                    <EyeToggle shown={shown} />
                    <span className={`text-sm ${shown ? "text-grey-700" : "text-grey-400"}`}>
                      {item.tip}
                    </span>
                  </button>
                );
              })
            ) : (
              <span className="text-sm text-grey-600">{AI_RESPONSE_COPY.NO_TIPS}</span>
            )}
          </div>
        </div>

        <div className="w-full h-[1px] bg-grey-200" />

        <div className="flex flex-col items-start gap-1">
          <span className="text-sm font-[400] text-grey-700">{AI_RESPONSE_COPY.TONES}</span>
          <div className="flex flex-row flex-wrap gap-2 mt-1">
            {loading ? (
              <CustomSkeleton skeletonCount={1} />
            ) : !isEmpty(tones) ? (
              tones.map(item => {
                const shown = shownToneIds.includes(item.id);
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => onToggleTone(item.id)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-md border border-solid text-sm ${
                      shown ? "border-grey-300 text-grey-700" : "border-grey-200 text-grey-400"
                    }`}
                  >
                    <EyeToggle shown={shown} />
                    {item.tip}
                  </button>
                );
              })
            ) : (
              <span className="text-sm text-grey-600">{AI_RESPONSE_COPY.NO_TONES}</span>
            )}
          </div>
        </div>

        {additionalCommentsEnabled && (
          <>
            <div className="w-full h-[1px] bg-grey-200" />
            <div className="flex flex-col items-start gap-1">
              <span className="text-sm font-[400] text-grey-400">
                {AI_RESPONSE_COPY.ADDITIONAL_NOTES_OPTIONAL}
              </span>
              <Input.TextArea
                disabled
                autoSize={{ minRows: 3 }}
                placeholder={AI_RESPONSE_COPY.ADDITIONAL_NOTES_PLACEHOLDER}
              />
            </div>
          </>
        )}
      </div>

      {/* Footer — Generate is disabled in hide/unhide mode */}
      <div className="flex flex-col gap-3 px-4 py-3 border-t border-solid border-grey-100">
        <Button disabled type="primary" className="w-full rounded-sm">
          {AI_RESPONSE_COPY.GENERATE}
        </Button>
      </div>
    </div>
  );
}

const itemShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  tip: PropTypes.string,
});

HideUnhidePanel.propTypes = {
  isMaximized: PropTypes.bool,
  onToggleMaximize: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  tips: PropTypes.arrayOf(itemShape),
  tones: PropTypes.arrayOf(itemShape),
  shownTipIds: PropTypes.arrayOf(PropTypes.number),
  shownToneIds: PropTypes.arrayOf(PropTypes.number),
  onToggleTip: PropTypes.func.isRequired,
  onToggleTone: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  additionalCommentsEnabled: PropTypes.bool,
};

HideUnhidePanel.defaultProps = {
  isMaximized: false,
  loading: false,
  tips: [],
  tones: [],
  shownTipIds: [],
  shownToneIds: [],
  saving: false,
  additionalCommentsEnabled: false,
};
